// @flow
import * as React from 'react';
import Box from 'components/Box';
import type { ImageData } from 'api/shared';
import { configString, configValue } from 'utils/config';
import { imageSizeUrl } from 'utils/filestack';
import { isIE11 } from 'utils/browser';
import { getJson } from 'utils/http';

const { useEffect, useState, useCallback, useRef } = React;

const importFilestack = () =>
  import(/* webpackChunkName: "filestack" */ 'filestack-js');

type Props = {
  name: string,
  onChange: (?ImageData) => void,
  aspectRatio?: number,
  disableCrop?: boolean,
  children: Object => React.Node,
  getDimensions?: boolean,
  label?: string
};

const getMockImage = () => ({
  filename: 'image.jpg',
  handle: configString('filestack', 'handleOverride'),
  mimetype: 'image/jpeg',
  size: 409609,
  source: 'local_file_system'
});

const FilestackPicker = ({
  name,
  onChange,
  aspectRatio,
  disableCrop,
  children,
  getDimensions,
  label,
  ...props
}: Props) => {
  const ref = useRef(null);

  const [dragActive, setDragActive] = useState(false);
  const [filestackClient, setFilestackClient] = useState(null);
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  // filestack is supposed to accept a node as container but it's not working
  const id = `fs-picker-${name.replace(/\./g, '_')}`;
  const inputId = `${id}-input`;

  const onMouseEnter = () => {
    setDragActive(true);
  };

  const onMouseLeave = () => {
    setDragActive(false);
  };

  const setInputId = useCallback(
    () => {
      // accessibility hack
      const el = ref.current;
      if (el) {
        const interval = window.setInterval(() => {
          const inputEl = el.querySelector('input');
          if (inputEl) {
            inputEl.id = inputId;
            inputEl.addEventListener('focus', onMouseEnter);
            inputEl.addEventListener('blur', onMouseLeave);
            window.clearInterval(interval);
          }
        }, 200);
      }
    },
    [inputId]
  );

  const onSuccess = useCallback(
    files => {
      setError(null);
      setLoading(false);
      const file = files[0];
      if (!file) return;
      const { filename, handle, mimetype, size, source } = file;
      const newImage = { filename, handle, mimetype, size, source };
      if (file && getDimensions) {
        getJson(imageSizeUrl({ handle: file.handle })).then(
          ({ width, height }) => {
            onChange({ ...newImage, width, height });
          }
        );
      } else {
        onChange(newImage);
      }
    },
    [getDimensions, onChange]
  );

  const onError = err => {
    if (err && err.length) {
      setError(
        isIE11()
          ? getText(
              'There was an error uploading your image. If the issue continues, we recommend trying a browser other than Internet Explorer.'
            )
          : getText('There was an error uploading your image.')
      );
    }
  };

  const getBaseOptions = useCallback(
    () => ({
      accept: 'image/*',
      fromSources: ['local_file_system'],
      uploadInBackground: false,
      transformations: {
        crop: aspectRatio
          ? {
              aspectRatio,
              force: true
            }
          : !disableCrop
      }
    }),
    [aspectRatio, disableCrop]
  );

  const fallbackClickHandler = () => {
    if (configValue('filestack', 'mock')) {
      onChange(getMockImage());
    }
  };

  const onUploadDone = res => {
    onSuccess(res.filesUploaded);
  };

  const onClick = () => {
    if (configValue('filestack', 'mock')) {
      onChange(getMockImage());
    } else {
      const options = {
        ...getBaseOptions(),
        onUploadDone,
        onUploadStarted: () => setLoading(true)
      };
      importFilestack().then(m => {
        const client = m.init(configValue('filestack', 'apiKey'));
        client.picker(options).open();
        setInputId();
      });
    }
  };

  useEffect(
    () => {
      if (!configValue('filestack', 'mock')) {
        const options = {
          ...getBaseOptions(),
          displayMode: 'dropPane',
          container: `#${id}`,
          dropPane: {
            cropFiles: !disableCrop,
            overlay: false,
            onSuccess,
            onError,
            onDragEnter: () => setDragActive(true),
            onDragLeave: () => setDragActive(false),
            onDrop: () => setDragActive(false),
            onProgress: () => setLoading(true)
          }
        };
        if (!filestackClient) {
          importFilestack().then(m => {
            const client = m.init(configValue('filestack', 'apiKey'));
            if (document.getElementById(id)) {
              client.picker(options).open();
              setFilestackClient(client);
              setInputId();
            }
          });
        }
      }
    },
    [disableCrop, filestackClient, getBaseOptions, id, onSuccess, setInputId]
  );

  return (
    <Box position="relative" {...props}>
      {children({ onClick, dragActive, error, loading })}
      <div ref={ref}>
        <Box component="label" display="none" htmlFor={inputId}>
          {label}
        </Box>
        <Box
          position="absolute"
          top="0"
          left="0"
          width="100%"
          height="100%"
          overflow="hidden"
          opacity="0"
          id={id}
          onClick={fallbackClickHandler}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        />
      </div>
    </Box>
  );
};

export default FilestackPicker;
